import type { Reducer } from '@reduxjs/toolkit';
import {
    APIS_ENUM,
    BDD_SWITCH_SOCIETES_IDS_TYPE,
    ENV,
    ENV_API,
    GOOGLE_MAPS_API_KEY,
} from '../../constants/env';
import {
    THEME_COLOR,
    DARK_THEME,
    SET_ID_SOCIETE,
    SET_ENV,
    type THEME_LIST,
    SET_ENV_API,
    SET_ALL_ENVS,
    SET_ID_PRESCRIPTEUR,
    SET_PRESCRIPTEURS,
    IS_AUTO_MODE_ENABLED,
} from '../../constants';
import { defaultEnv } from './defaultEnv';
import { Prescripteur } from '../tiers';

export type CustomizerReducerActiveModeType = 'light' | 'dark';
export type CustomizerReducerActiveThemeType = THEME_LIST;

export type CustomizerReducetApiType = {
    achats: ENV_API;
    tiers: ENV_API;
    dossiers: ENV_API;
    offre: ENV_API;
    formalite: ENV_API;
    facturation: ENV_API;
    sign: ENV_API;
    mail: ENV_API;
    formulaire: ENV_API;
    utilisateur: ENV_API;
    wsMessagerie: ENV_API;
    annoncesLegales: ENV_API;
    societe: ENV_API;
    geoloc: ENV_API;
    s3avatar: ENV_API;
    siren: ENV_API;
    iaServices: ENV_API;
    [apiName in APIS_ENUM]: ENV_API;
};

export type CustomizerReducetAppType = {
    annoncesLegales: ENV_API;
    l2dp: ENV_API;
};

export type CustomizerReducerType = {
    isAutoModeEnabled: boolean;
    activeMode: CustomizerReducerActiveModeType; // This can be light or dark
    activeTheme: CustomizerReducerActiveThemeType; // BLUE_THEME, GREEN_THEME, RED_THEME, BLACK_THEME, PURPLE_THEME, INDIGO_THEME, ORANGE_THEME
    xIdSociete: BDD_SWITCH_SOCIETES_IDS_TYPE;
    prescripteurs: Array<{
        id: Prescripteur['id'];
        raisonSociale: Prescripteur['raisonSociale'];
        siret: Prescripteur['siret'];
        siren: Prescripteur['siren'];
    }>;
    idPrescripteur: number | undefined;
    sideBarState: 'open' | 'close';
    env: ENV;
    api: CustomizerReducetApiType;
    app: CustomizerReducetAppType;
    googleMapApiKey: string | null;
};

const customizerLocalStorageState = localStorage.getItem('customizerState');

export const INIT_STATE: CustomizerReducerType = {
    isAutoModeEnabled: false,
    activeMode: 'light', // This can be light or dark
    activeTheme: 'Original', // BLUE_THEME, GREEN_THEME, RED_THEME, BLACK_THEME, PURPLE_THEME, INDIGO_THEME, ORANGE_THEME, L2D_THEME
    xIdSociete: 2,
    idPrescripteur: undefined,
    prescripteurs: [],
    sideBarState: 'open',
    env: defaultEnv,
    api: {
        achats: defaultEnv,
        tiers: defaultEnv,
        dossiers: defaultEnv,
        offre: defaultEnv,
        formalite: defaultEnv,
        facturation: defaultEnv,
        sign: defaultEnv,
        mail: defaultEnv,
        utilisateur: defaultEnv,
        formulaire: defaultEnv,
        wsMessagerie: defaultEnv,
        annoncesLegales: defaultEnv,
        societe: defaultEnv,
        geoloc: defaultEnv,
        s3avatar: defaultEnv,
        siren: defaultEnv,
        iaServices: defaultEnv,
    },
    app: {
        annoncesLegales: defaultEnv,
        l2dp: defaultEnv,
    },
    googleMapApiKey: GOOGLE_MAPS_API_KEY[defaultEnv] || null,
};

let state = {
    ...INIT_STATE,
};

if (customizerLocalStorageState && customizerLocalStorageState !== '') {
    try {
        state = {
            ...INIT_STATE,
            ...(JSON.parse(customizerLocalStorageState) as CustomizerReducerType),
        };

        if (localStorage.getItem('customizerState') !== JSON.stringify(state)) {
            localStorage.setItem('customizerState', JSON.stringify(state));
            window.location.reload();
        }
    } catch (e) {
        localStorage.setItem('customizerState', JSON.stringify(INIT_STATE));
        console.warn(e);
        window.location.reload();
    }
}

export const CustomizerReducer: Reducer<CustomizerReducerType> = (
    customizerState = state,
    action = { type: 'nothing' },
) => {
    let newState;
    let mustReload = false;
    let mustSave = false;
    switch (action.type) {
        case IS_AUTO_MODE_ENABLED:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                isAutoModeEnabled: action.payload,
            };
            mustSave = true;
            break;
        case DARK_THEME:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                activeMode: action.payload,
            };
            mustSave = true;
            break;
        case THEME_COLOR:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                activeTheme: action.payload,
            };
            mustSave = true;
            break;
        case SET_ID_SOCIETE:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                xIdSociete: action.payload,
            };
            mustSave = true;
            break;
        case SET_ID_PRESCRIPTEUR:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                idPrescripteur: action.payload,
            };
            mustSave = true;
            mustReload = true;
            break;
        case SET_PRESCRIPTEURS:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                prescripteurs: action.payload,
            };
            mustSave = true;
            break;
        case SET_ALL_ENVS:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                env: action.payload.keycloak,
                api: action.payload.apis,
                app: action.payload.apps,
            };
            mustSave = true;
            mustReload = true;
            break;
        case SET_ENV:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                env: action.payload,
            };
            mustSave = true;
            mustReload = true;
            break;

        case SET_ENV_API:
            newState = {
                ...INIT_STATE,
                ...customizerState,
                api: { ...INIT_STATE.api, ...customizerState.api },
            };

            if (action.payload.api && action.payload.env) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const overide: any = {};
                overide[action.payload.api as APIS_ENUM] = action.payload.env as ENV_API;
                newState.api = {
                    ...INIT_STATE.api,
                    ...newState.api,
                    ...overide,
                };
            }
            mustReload = true;
            mustSave = true;
            break;
        default:
            newState = { ...customizerState };
            break;
    }

    if (mustSave) {
        localStorage.setItem('customizerState', JSON.stringify(newState));
    }

    if (mustReload) {
        window.location.reload();
    }

    return newState;
};

export default CustomizerReducer;
